<template>
  <div>
    <div class="title mb-2">Access Levels</div>
    <p>Here you can set the access levels for this special occurrence. All members of the board will have the same access level.</p>
    <dl class="shadow-sm bg-white p-3">
      <dt>Admin</dt>
      <dl>Full control (including linked tasks)</dl>

      <dt>Editor</dt>
      <dl>
        Full control (including linked tasks)
        <strong>except</strong>
        modifying access rights
      </dl>
      <dt>Contributor</dt>
      <dl>Can access all linked tasks, can create items for own board, can <strong>not</strong> modify/ complete the special occurrence</dl>

      <dt>None</dt>
      <dl>Special Occurrence is not visible</dl>
    </dl>
    <table class="table table sm bg-white shadow-sm">
      <thead>
        <tr>
          <th>Board</th>
          <th>Access Level</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="accessLevel in accessLevels" :key="accessLevel.boardId">
          <td class="font-weight-bold">
            {{ accessLevel.boardName }}
            <span v-if="accessLevel.boardId === occurrence.ownerBoardId">
              <strong><span class="badge badge-success">Owner</span></strong>
            </span>
          </td>
          <td>
            <div
              class="badge mr-2"
              :class="{ 'badge-primary': accessLevel.accessLevel === 'None' }"
              @click="setAccess(accessLevel.boardId, 'None')"
            >
              None
            </div>
            <div
              class="badge mr-2"
              :class="{ 'badge-success': accessLevel.accessLevel === 'Admin' }"
              @click="setAccess(accessLevel.boardId, 'Admin')"
            >
              Admin
            </div>
            <div
              class="badge mr-2"
              :class="{ 'badge-success': accessLevel.accessLevel === 'Editor' }"
              @click="setAccess(accessLevel.boardId, 'Editor')"
            >
              Editor
            </div>
            <div
              class="badge mr-2"
              :class="{ 'badge-success': accessLevel.accessLevel === 'Contributor' }"
              @click="setAccess(accessLevel.boardId, 'Contributor')"
            >
              Contributor
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { SpecialOccurrenceModule, SpecialOccurrenceStore } from '@/store/specialOccurrence';
  import AttachmentView from '@/components/Attachments/AttachmentView.vue';
  import UploadAttachment from '@/components/Attachments/UploadAttachment.vue';
  import {
    SpecialOccurrenceAccessLevelDto,
    SpecialOccurrenceAccessLevelUpdateDto,
    SpecialOccurrenceDto
  } from "@/apiclient/apiclient_generated";
  import { GlobalHelper } from '@/helpers/global-helper';

  @Component({
    components: {
      UploadAttachment,
      AttachmentView,
    },
  })
  export default class SpecialOccurrenceAccess extends Vue {
    private store: SpecialOccurrenceModule = SpecialOccurrenceStore;

    private get occId() {
      return this.$route.params.occId;
    }

    get occurrence(): SpecialOccurrenceDto {
      return this.store.currentOccurrence;
    }

    get accessLevels(): SpecialOccurrenceAccessLevelDto[] {
      return this.store.currentOccurrenceAccessLevels;
    }

    async setAccess(boardId: string, accessLevel: any) {
      if (boardId == this.occurrence.ownerBoardId)
      {
        GlobalHelper.NotifyError('Access level of the owner board can not be changed - always admin');
      } else {

        const dto = new SpecialOccurrenceAccessLevelUpdateDto();
        dto.accessLevel = accessLevel;
        dto.specialOccurrenceId = this.occId;
        dto.boardId = boardId;
        await this.store.setAccessLevel(dto);
        GlobalHelper.NotifySuccess('Access level set');
      }
    }

    created() {
      this.store.getAccessLevels(this.$route.params.occId);
    }
  }
</script>

<style scoped lang="scss">
  .title {
    font-weight: bold;
    font-size: 1.1rem;
    text-decoration: none;
  }

  .badge {
    font-size: 0.8rem;
    cursor: pointer;
    border: solid 1px darken(#eaeaea, 5%);

    &:hover {
      background: #28a745;
      color: white;
    }
  }
</style>
